import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const YEAR_SERIES_COLORS = [
  '#0f766e',
  '#2563eb',
  '#7c3aed',
  '#ea580c',
  '#dc2626',
  '#0891b2',
  '#65a30d',
  '#c026d3'
];

const formatNumber = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }

  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (absoluteValue >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (absoluteValue >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return value.toLocaleString('en-US', {
    maximumFractionDigits: 2
  });
};

const hasMetricData = (chartData, metricKey) =>
  chartData.some((row) => typeof row[metricKey] === 'number' && Number.isFinite(row[metricKey]));

const formatMetricLabel = (metricKey) =>
  metricKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const pivotChartData = (chartData, selectedMetrics, metricLabels) => {
  const years = chartData.map((row) => row.fiscalYear);
  const metrics = selectedMetrics
    .filter((key) => hasMetricData(chartData, key))
    .map((key) => ({ key, label: metricLabels[key] || formatMetricLabel(key) }));

  return {
    years,
    rows: metrics.map(({ key, label }) => {
      const row = { metric: label };

      chartData.forEach((yearRow) => {
        row[yearRow.fiscalYear] = yearRow[key];
      });

      return row;
    })
  };
};

export default function IncomeStatementChart({
  chartData = [],
  metricLabels = {},
  selectedMetrics = [],
  selectedStock
}) {
  if (!chartData.length) {
    return <p>No income statement data loaded for {selectedStock}.</p>;
  }

  const { years, rows } = pivotChartData(chartData, selectedMetrics, metricLabels);

  if (!rows.length) {
    return <p>Select at least one available income statement metric to show the chart.</p>;
  }

  return (
    <div className="chart-wrapper">
      <h3>{selectedStock} Income Statement</h3>
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={rows} margin={{ top: 16, right: 16, left: 16, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="metric" />
          <YAxis tickFormatter={formatNumber} width={90} />
          <Tooltip formatter={(value, name) => [formatNumber(value), name]} />
          <Legend />
          {years.map((year, index) => (
            <Bar
              key={year}
              dataKey={year}
              name={year}
              fill={YEAR_SERIES_COLORS[index % YEAR_SERIES_COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
